/* -------------------------- Design imports start -------------------------- */
import {
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { type ReactElement, useEffect, useState } from "react";

import LogTool from '../../logger/logTools'
/* ------------------------- Functional imports end ------------------------- */

type UnitOption = {
  value: string
  label: string
}

type Props = {
  id?: string
  value: string
  limits?: { minInput?: number; maxInput?: number }
  label: string
  labelWidth?: number
  onChange: (value: number | string) => void
  onUnitChange?: (unit: string) => void
  validation?: boolean
  errorMessage?: string
  disabled?: boolean
  required?: boolean
  showError?: boolean
  unit?: string
  unitOptions?: UnitOption[]
  currentUnit?: string
}
/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function NumberInputWithUnit(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const {
    id,
    label,
    unit = '',
    value,
    onChange,
    onUnitChange,
    disabled = false,
    required = false,
    errorMessage = '',
    validation = true,
    showError = true,
    unitOptions,
    currentUnit,
  } = props
  let { limits = { minInput: Number.MIN_SAFE_INTEGER, maxInput: Number.MAX_SAFE_INTEGER } } = props

  // parse limits
  if (!Object.hasOwn(limits, 'minInput')) {
    limits = { minInput: Number.MIN_SAFE_INTEGER, maxInput: limits.maxInput }
  }
  if (!Object.hasOwn(limits, 'maxInput')) {
    limits = { minInput: limits.minInput, maxInput: Number.MAX_SAFE_INTEGER }
  }

  const log = new LogTool({ context: 'NumberInputWithUnit', enable: true, logLevel: 'warn' })

  const isValidInput = (input: string | number): boolean => {
    if (typeof input === 'undefined') {
      log.debug(`isValidInput(${input})`, 'Input is undefined, return true.')
      return true
    }

    const inputStr = typeof input === 'number' ? input.toString() : input

    if (inputStr === '') {
      log.debug(`isValidInput(${inputStr})`, 'Input is empty string, return true.')
      return true
    }
    if (!/^[0-9]+((.|,)[0-9]+)?$/.test(inputStr)) {
      log.debug(
        `isValidInput(${inputStr})`,
        'Input does not only contain numbers and decimal delimiters, return false.'
      )
      return false
    }

    const parsedInput = parseFloat(inputStr.replace(',', '.'))
    if (Number.isNaN(parsedInput)) {
      log.debug(`isValidInput(${inputStr})`, 'Input is NaN, return false.')
      return false
    }

    const hasMinLimit = limits.minInput !== undefined && limits.minInput !== Number.MIN_SAFE_INTEGER
    const hasMaxLimit = limits.maxInput !== undefined && limits.maxInput !== Number.MAX_SAFE_INTEGER

    if (hasMinLimit && parsedInput < limits.minInput!) {
      log.debug(
        `isValidInput(${inputStr})`,
        `Input ${parsedInput} is below minimum ${limits.minInput}, return false.`
      )
      return false
    }
    if (hasMaxLimit && parsedInput >= limits.maxInput!) {
      log.debug(
        `isValidInput(${inputStr})`,
        `Input ${parsedInput} is at or above maximum ${limits.maxInput}, return false.`
      )
      return false
    }

    log.debug(`isValidInput(${inputStr})`, 'Input is valid, return true.')
    return true
  }
  /* --------------------------- Non state data end --------------------------- */
  /* ---------------------------- Flag states start --------------------------- */
  const [error, setError] = useState<boolean>(
    (validation && !isValidInput(value)) || (required && !value)
  )
  /* ----------------------------- Flag states end ---------------------------- */

  useEffect(() => {
    if (required && value === '') setError(true)
  }, [value, validation])

  // Determine the unit to display/use
  const displayUnit = currentUnit || unit
  const hasUnitOptions = unitOptions && unitOptions.length > 0

  log.debug('value ->', value, ', error ->', error, ', displayUnit ->', displayUnit)
  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <FormControl fullWidth variant="outlined">
      <InputLabel>{label}</InputLabel>
      <OutlinedInput
        type="number"
        id={id || label}
        value={value || ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          onChange(e.currentTarget.value)
          log.debug(
            `onChange(${e.currentTarget.value})`,
            'Typeof ->',
            typeof e.currentTarget.value,
            ', parseFloat(value) ->',
            parseFloat(e.currentTarget.value)
          )
          validation && setError(!isValidInput(e.currentTarget.value))
        }}
        label={label}
        autoComplete="number"
        disabled={disabled}
        required={required}
        error={error && showError}
        aria-describedby="text-input"
        inputProps={{
          'aria-label': 'weight',
          /* defaultValue: '1',
            min: '0',
            step: '1', */
        }}
        endAdornment={
          hasUnitOptions ? (
            <InputAdornment position="end">
              <Select
                value={displayUnit}
                onChange={(e) => {
                  const newUnit = e.target.value
                  if (onUnitChange) {
                    onUnitChange(newUnit)
                  }
                }}
                disabled={disabled}
                sx={{
                  '& .MuiSelect-select': {
                    padding: '8px 32px 8px 12px',
                    minWidth: '60px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              >
                {unitOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </InputAdornment>
          ) : (
            <InputAdornment position="end">{displayUnit}</InputAdornment>
          )
        }
      />
      {error && (
        <FormHelperText error variant="outlined">
          {errorMessage}
        </FormHelperText>
      )}
    </FormControl>
  )
}
